import { Injectable } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { TemplateObservable } from '@bazis/shared/classes/template-observable';
import isEqual from 'lodash.isequal';
import { Toast } from '@bazis/shared/models/toast.types';

/**
 * Supported toast placement groups.
 * Each group maps to a list inside `toastGroups`.
 */
export const TOAST_GROUPS = ['top', 'bottom', 'center', 'middle'];

/**
 * **BazisToastService**
 *
 * Creates and manages toast notifications.
 * Stores active toasts grouped by position and automatically removes expired toasts.
 */
@Injectable({
    providedIn: 'root',
})
export class BazisToastService {
    /**
     * Default toast properties applied to every created toast.
     * Values passed to `create()` override these defaults.
     */
    protected _defaultParams: Toast = {
        title: '',
        message: '',
        titleKey: '',
        messageKey: '',
        cssClass: '',
        duration: 5000,
        buttons: [],
        position: 'bottom',
        animated: false,
        icon: '',
        color: '',
        id: '',
        createdAt: 0,
        stack: false,
        closeIcon: 'cross-small',
    };

    /**
     * Active toasts grouped by position.
     * Keys match `TOAST_GROUPS`.
     */
    toastGroups = new TemplateObservable({
        top: [],
        bottom: [],
        center: [],
        middle: [],
    });

    /**
     * Default visual properties per toast `type`.
     * Applied by `create()` when `params.type` is present.
     */
    protected _typeProperties = {
        success: {
            icon: 'check',
            color: 'success',
            position: 'top',
        },
        error: {
            icon: 'cross',
            color: 'danger',
            position: 'top',
            stack: true,
        },
        warning: {
            icon: 'exclamation',
            color: 'warning',
            position: 'top',
        },
    };

    /**
     * Starts internal timer that removes expired toasts.
     */
    constructor() {
        this.startCounter();
    }

    /**
     * Periodically removes toasts that have expired (`dismissAt <= now`).
     *
     * Uses a 1-second interval.
     *
     * @returns void
     */
    protected startCounter() {
        setInterval(() => {
            const currentTime = new Date().getTime();
            const toastGroups = this.toastGroups._;
            TOAST_GROUPS.forEach((group) => {
                toastGroups[group] = toastGroups[group].filter((v) => v.dismissAt > currentTime);
            });
            this.toastGroups.set(toastGroups);
        }, 1000);
    }

    /**
     * Create a toast and add it to the target group.
     *
     * Behavior:
     * - If `params.type` matches `_typeProperties`, merges those properties in.
     * - Generates `id` with `uuidv4()` if missing.
     * - Sets `createdAt` and computes `dismissAt = createdAt + duration`.
     * - Adds to `toastGroups` using `stackToasts()` logic.
     *
     * @param params - Partial toast data to apply.
     * @returns Created toast object (`Toast`).
     */
    create(params: Partial<Toast>): Toast {
        if (params.type && this._typeProperties[params.type]) {
            params = {
                ...params,
                ...this._typeProperties[params.type],
            } as Partial<Toast>;
        }

        params = {
            ...this._defaultParams,
            ...params,
            id: params.id || uuidv4(),
            createdAt: new Date().getTime(),
        };
        params.dismissAt = params.createdAt + params.duration;
        this.toastGroups.set(this.stackToasts(params));
        return params;
    }

    /**
     * Add a toast to its group, with optional “stack” behavior.
     *
     * When `params.stack` is true and the last toast in the group is also stackable,
     * the method compares the last toast with the new one (ignoring time/id) and:
     * - Adds a new toast if content is different
     * - Otherwise, only extends the last toast lifetime (`createdAt`/`dismissAt`)
     *
     * @param params - Toast object to place into groups.
     * @returns Updated toast groups object.
     */
    protected stackToasts(params) {
        const toastGroups = this.toastGroups._;
        const targetGroup = toastGroups[params.position];
        let addToGroup = !params.stack || targetGroup.length === 0;
        if (!addToGroup) {
            const lastElement = targetGroup[targetGroup.length - 1];
            if (!lastElement.stack) {
                addToGroup = true;
            } else {
                const copy = { ...lastElement };
                copy.createdAt = params.createdAt;
                copy.dismissAt = params.dismissAt;
                copy.id = params.id;
                addToGroup = !isEqual(copy, params);
            }
        }
        if (addToGroup) {
            targetGroup.push(params);
        } else {
            targetGroup[targetGroup.length - 1].createdAt = params.createdAt;
            targetGroup[targetGroup.length - 1].dismissAt = params.dismissAt;
        }
        return toastGroups;
    }

    /**
     * Dismiss a toast by id.
     *
     * When `group` is provided, removes only from that group.
     * Otherwise, searches and removes from all groups.
     *
     * @param id - Toast id string.
     * @param group - Optional group name (one of `TOAST_GROUPS`). Default: `null`.
     * @returns void
     */
    dismiss(id, group = null) {
        const toastGroups = this.toastGroups._;
        if (group) {
            toastGroups[group] = toastGroups[group].filter((v) => v.id !== id);
        } else {
            TOAST_GROUPS.forEach((toastGroup: string) => {
                toastGroups[toastGroup] = toastGroups[toastGroup].filter((v) => v.id !== id);
            });
        }
        this.toastGroups.set(toastGroups);
    }

    // TODO: dismiss all toast
    /**
     * Placeholder for “dismiss all” functionality.
     *
     * @param position - Optional group name to clear. Default: `null`.
     * @returns void
     */
    dismissAllToast(position = null) {
        if (position) {
        } else {
        }
    }
}
